import { IoPersonOutline } from 'react-icons/io5';
import { Link } from 'common/components/Link/animated';
import { IAnyType } from 'common/types/common';
import { Article, Tag } from 'common/types/content/graphql/graphql';
import { formatDate } from 'common/utils/date';
import { getEmployeeProfileHref } from 'common/utils/employeeProfile';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

export const ContentArticleItem: React.FC<{ article: Article }> = ({ article }) => {
  const { i18n } = useTranslation();
  const { author, date, description, slug: slugRaw, tags, title, cover } = article;
  const coverSrc = cover?.formats?.large?.url || cover?.url || '';

  const renderAuthorImage = () => {
    if (
      author?.avatar?.formats?.small?.url ||
      author?.avatar?.formats?.thumbnail?.url ||
      author?.avatar?.url
    ) {
      const avatarUrl =
        author?.avatar?.formats?.small?.url ||
        author?.avatar?.formats?.thumbnail?.url ||
        author?.avatar?.url;
      return (
        <Image
          alt={author.avatar.alternativeText || ''}
          className="mr-2 h-[30px] w-[30px] rounded-full bg-gray-800 text-white"
          src={avatarUrl}
          height={author.avatar.height || 0}
          width={author.avatar.width || 0}
        />
      );
    }
    return (
      <div className="mr-2 flex h-[30px] w-[30px] items-center justify-center rounded-full bg-primary/10">
        <IoPersonOutline color="white" size={14} />
      </div>
    );
  };

  const renderAuthor = () => {
    if (author?.name === 'Ivan Vukušić') {
      const employeeHref = getEmployeeProfileHref(author?.name, i18n.language);
      return (
        <Link href={employeeHref} className="flex items-center text-white">
          {renderAuthorImage()}
          <div>{author.name}</div>
        </Link>
      );
    }
    return (
      <div className="flex items-center text-white">
        {renderAuthorImage()}
        <div>{author?.name || '-'}</div>
      </div>
    );
  };

  const slug = slugRaw ? `/blog/${slugRaw}` : '';
  const classNameTag =
    'bg-white/10 p-3 py-2 text-[16px] hover:border-primary hover:text-primary p-2 py-1 mr-1 mb-1 hover:border-primary hover:text-primary cursor-pointer caret-white text-white';
  return (
    <article
      key={article.documentId}
      className="flex h-full flex-col justify-between border-[1px] border-white/10 bg-[#261434] pb-4">
      <div>
        <Link href={slug}>
          <div className="group relative min-h-[200px] overflow-hidden lg:h-[340px] 2xl:h-[400px]">
            {!!coverSrc && (
              <Image
                src={coverSrc}
                className="h-full w-full object-cover object-center"
                alt={cover?.alternativeText || ''}
                height={cover?.height || 0}
                width={cover?.width || 0}
              />
            )}
            <div className="absolute bottom-0 left-[50%] top-0 w-0 bg-[#261434] opacity-0 transition-all duration-500 group-hover:w-[100%] group-hover:-translate-x-[50%] group-hover:opacity-50" />
          </div>
        </Link>
        <div className="px-4">
          <div className="mt-3">
            <div className="mt-1 flex flex-col text-white">
              {renderAuthor()}
              <div className="flex flex-row items-center text-[16px] leading-4 text-white pl-[36px]">
                {formatDate(date, 'LL', i18n.language)}
              </div>
            </div>
          </div>
          <Link href={slug}>
            <h2 className="text-heading-item mb-1 max-w-xl pt-4 font-bold text-white">{title}</h2>
          </Link>
          <div className="mb-3 max-w-2xl pt-2 text-white/90">{description}</div>
        </div>
      </div>
      <div className="mt-6 flex flex-wrap px-4">
        {tags.map(t => {
          const tag = t as IAnyType as Tag;
          if (tag.href) {
            return (
              <Link className={classNameTag} href={tag.href} key={tag.slug}>
                {tag.name}
              </Link>
            );
          }
          return (
            <div className={classNameTag} key={tag.slug}>
              {tag.name}
            </div>
          );
        })}
      </div>
    </article>
  );
};
